import React, { useState, useEffect } from 'react';
import { 
  User, CreditCard, Crown, Calendar, Settings, AlertCircle,
  CheckCircle, Plus, Trash2, Edit, Download, RefreshCw,
  Shield, Zap, Star, MessageCircle, FileText, HeadphonesIcon,
  Phone, Mail, Clock, ChevronRight, CheckCircle2, XCircle
} from 'lucide-react';
import { I18nProvider, useI18n } from '../hooks/useI18n';
import { useTheme } from '../contexts/ThemeContext';
import { useAuthStore } from '../stores/authStore'; // ✅ IMPORTAR STORE

// ===============================================
// 🔧 INTERFACES Y TIPOS
// ===============================================

interface UserData {
  id: string;
  name: string;
  email: string;
  subscription_plan?: string;
  subscription_status?: 'active' | 'inactive' | 'expired' | 'trial';
  subscription_expires_at?: string;
  subscription_period?: 'monthly' | 'annual';
  is_premium?: boolean;
  created_at?: string;
}

interface SubscriptionPlan {
  plan_code: string;
  plan_name: string;
  price_monthly: number;
  price_annual: number;
  features: string[];
  limits: {
    daily_queries: number;
    whatsapp_support: boolean;
    priority_support: boolean;
    documentation_access: boolean;
    consultation_sessions?: boolean;
  };
  is_popular: boolean;
  annual_discount?: number;
}

interface MyAccountProps {
  currentUser: User | null;
  onUserUpdate: (user: User) => void;
}

// ===============================================
// 🎨 COMPONENTE PRINCIPAL MY ACCOUNT
// ===============================================

const MyAccount: React.FC = () => { // ✅ SIN PROPS
  const { isDark } = useTheme();
  const { user, updateUser } = useAuthStore(); // ✅ USAR STORE
  
  // Estados principales
  const [activeSection, setActiveSection] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [availablePlans, setAvailablePlans] = useState<SubscriptionPlan[]>([]);
  const [selectedBilling, setSelectedBilling] = useState<'monthly' | 'annual'>('monthly');
  const [showPlanChange, setShowPlanChange] = useState(false);
  const { t } = useI18n();
  
  // Estados de toast
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');
  const [toastVisible, setToastVisible] = useState(false);

  // ✅ CARGAR PERFIL REAL DEL USUARIO
  const [currentUser, setCurrentUser] = useState<UserData | null>(null);

  useEffect(() => {
    loadUserProfile();
    loadAvailablePlans();
  }, []);

  const loadUserProfile = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('kikai_token');
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/user/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Error cargando perfil');

      const data = await response.json();
      if (data.success && data.user) {
        setCurrentUser(data.user);
      }
    } catch (error) {
      console.error('❌ Error cargando perfil:', error);
      showToast('Error cargando perfil de usuario', 'error');
    }
  };

  const loadAvailablePlans = async () => {
    try {
      const response = await fetch('/api/subscription-plans', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('kikai_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailablePlans(data.data?.plans || []);
      }
    } catch (error) {
      console.error('Error cargando planes:', error);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 3000);
  };

  const changePlan = async (newPlanCode: string, billingPeriod: 'monthly' | 'annual') => {
    try {
      setLoading(true);
      
      const response = await fetch('/api/subscriptions/change-plan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('kikai_token')}`
        },
        body: JSON.stringify({
          newPlanCode,
          billingPeriod
        })
      });

      if (response.ok) {
        const updatedUser = { 
          ...currentUser, 
          subscription_plan: newPlanCode,
          subscription_period: billingPeriod,
          subscription_status: 'active'
        } as User;
        
        onUserUpdate(updatedUser);
        setShowPlanChange(false);
        showToast('¡Plan actualizado exitosamente!', 'success');
      } else {
        throw new Error('Error cambiando plan');
      }
    } catch (error) {
      showToast('Error actualizando plan', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ===============================================
  // 🎨 COMPONENTES DE RENDERIZADO
  // ===============================================

  const getCurrentPlanData = (): SubscriptionPlan | null => {
    const currentPlanCode = currentUser?.subscription_plan || 'free';
    return availablePlans.find(plan => plan.plan_code === currentPlanCode) || null;
  };

  const getPlanIcon = (planCode: string) => {
    const icons = {
      free: Shield,
      standard: Zap,
      professional: Star,
      premium: Crown
    };
    return icons[planCode as keyof typeof icons] || Shield;
  };

  const getPlanColor = (planCode: string) => {
    const colors = {
      free: 'gray',
      standard: 'blue',
      professional: 'purple',
      premium: 'yellow'
    };
    return colors[planCode as keyof typeof colors] || 'gray';
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'No disponible';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // ===============================================
  // 📊 SECCIÓN DE OVERVIEW
  // ===============================================

  const renderOverviewSection = () => {
    const currentPlan = getCurrentPlanData();
    const planIcon = getPlanIcon(currentUser?.subscription_plan || 'free');
    const PlanIcon = planIcon;

    return (
      <div className="space-y-6">
        {/* Estado Actual del Plan */}
        <div className={`rounded-2xl border p-6 ${
          isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Estado de Suscripción
            </h3>
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
              currentUser?.subscription_status === 'active' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {currentUser?.subscription_status === 'active' ? 'Activa' : 'Inactiva'}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  getPlanColor(currentUser?.subscription_plan || 'free') === 'yellow' 
                    ? 'bg-yellow-100 text-yellow-600'
                    : getPlanColor(currentUser?.subscription_plan || 'free') === 'purple'
                    ? 'bg-purple-100 text-purple-600'
                    : getPlanColor(currentUser?.subscription_plan || 'free') === 'blue'
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  <PlanIcon className="w-6 h-6" />
                </div>
                <div>
                  <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Plan {currentPlan?.plan_name || 'Gratuito'}
                  </h4>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {currentUser?.subscription_period === 'annual' ? 'Facturación anual' : 'Facturación mensual'}
                  </p>
                </div>
              </div>

              {currentPlan && (
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Precio actual:
                    </span>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      ${currentUser?.subscription_period === 'annual' 
                        ? currentPlan.price_annual 
                        : currentPlan.price_monthly}
                      /{currentUser?.subscription_period === 'annual' ? 'año' : 'mes'}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Renovación:
                    </span>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {formatDate(currentUser?.subscription_expires_at)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <h5 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Características del Plan:
              </h5>
              <div className="space-y-2">
                {currentPlan?.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {currentUser?.subscription_plan === 'free' && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => setShowPlanChange(true)}
                className="w-full bg-gradient-to-r from-purple-500 to-blue-500 text-white py-3 px-6 rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all font-medium"
              >
                <Crown className="w-5 h-5 inline mr-2" />
                Actualizar a Plan Premium
              </button>
            </div>
          )}

          {currentUser?.subscription_plan !== 'free' && (
            <div className="mt-6 pt-6 border-t border-gray-200 flex space-x-3">
              <button
                onClick={() => setShowPlanChange(true)}
                className={`flex-1 py-2 px-4 rounded-lg border transition-all ${
                  isDark 
                    ? 'border-gray-600 hover:bg-gray-700 text-gray-300' 
                    : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                }`}
              >
                Cambiar Plan
              </button>
              <button
                className={`flex-1 py-2 px-4 rounded-lg border transition-all ${
                  isDark 
                    ? 'border-red-600 hover:bg-red-900 text-red-400' 
                    : 'border-red-300 hover:bg-red-50 text-red-700'
                }`}
              >
                Cancelar Suscripción
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ===============================================
  // 💳 SECCIÓN DE PLANES
  // ===============================================

  const renderPlansSection = () => {
    const currentPlanCode = currentUser?.subscription_plan || 'free';

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Planes de Suscripción
          </h3>
          <div className={`flex rounded-lg p-1 ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <button
              onClick={() => setSelectedBilling('monthly')}
              className={`px-3 py-1 text-sm rounded-md transition-all ${
                selectedBilling === 'monthly'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : isDark ? 'text-gray-400' : 'text-gray-600'
              }`}
            >
              Mensual
            </button>
            <button
              onClick={() => setSelectedBilling('annual')}
              className={`px-3 py-1 text-sm rounded-md transition-all ${
                selectedBilling === 'annual'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : isDark ? 'text-gray-400' : 'text-gray-600'
              }`}
            >
              Anual
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {availablePlans.map((plan) => {
            const PlanIcon = getPlanIcon(plan.plan_code);
            const isCurrentPlan = plan.plan_code === currentPlanCode;
            const price = selectedBilling === 'annual' ? plan.price_annual : plan.price_monthly;
            const period = selectedBilling === 'annual' ? 'año' : 'mes';

            return (
              <div
                key={plan.plan_code}
                className={`relative rounded-2xl border-2 p-6 transition-all ${
                  isCurrentPlan
                    ? isDark 
                      ? 'border-purple-500 bg-purple-900/20' 
                      : 'border-purple-500 bg-purple-50'
                    : plan.is_popular
                    ? isDark
                      ? 'border-blue-500 bg-blue-900/20'
                      : 'border-blue-500 bg-blue-50'
                    : isDark
                    ? 'border-gray-700 bg-gray-800 hover:border-gray-600'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                {plan.is_popular && !isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                      Más Popular
                    </span>
                  </div>
                )}

                {isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                      Plan Actual
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 ${
                    getPlanColor(plan.plan_code) === 'yellow' 
                      ? 'bg-yellow-100 text-yellow-600'
                      : getPlanColor(plan.plan_code) === 'purple'
                      ? 'bg-purple-100 text-purple-600'
                      : getPlanColor(plan.plan_code) === 'blue'
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    <PlanIcon className="w-6 h-6" />
                  </div>
                  
                  <h4 className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {plan.plan_name}
                  </h4>
                  
                  <div className="mt-2">
                    <span className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      ${price}
                    </span>
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      /{period}
                    </span>
                  </div>

                  {selectedBilling === 'annual' && plan.annual_discount && (
                    <div className="text-sm text-green-600 mt-1">
                      Ahorra {plan.annual_discount}%
                    </div>
                  )}
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => !isCurrentPlan && changePlan(plan.plan_code, selectedBilling)}
                  disabled={isCurrentPlan || loading}
                  className={`w-full py-2 px-4 rounded-lg font-medium transition-all ${
                    isCurrentPlan
                      ? isDark 
                        ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : plan.is_popular
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : isDark
                      ? 'bg-gray-700 text-white hover:bg-gray-600'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  {loading ? (
                    <RefreshCw className="w-4 h-4 animate-spin mx-auto" />
                  ) : isCurrentPlan ? (
                    'Plan Actual'
                  ) : (
                    'Seleccionar Plan'
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ===============================================
  // 💬 SECCIÓN DE ATENCIÓN PERSONALIZADA
  // ===============================================

  const renderPersonalizedSupportSection = () => {
    return (
      <div className="space-y-6">
        <div className={`rounded-2xl border p-6 ${
          isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-green-100 text-green-600 rounded-xl flex items-center justify-center">
              <MessageCircle className="w-6 h-6" />
            </div>
            <div>
              <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Atención Personalizada WhatsApp
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Soporte directo con nuestros técnicos especializados
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className={`p-4 rounded-lg ${isDark ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'}`}>
              <div className="flex items-center space-x-2 mb-2">
                <Phone className="w-5 h-5 text-green-600" />
                <span className={`font-medium ${isDark ? 'text-green-400' : 'text-green-800'}`}>
                  Contacto Directo
                </span>
              </div>
              <p className={`text-sm ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                Conecta directamente con técnicos especializados en motores Caterpillar
                disponibles de 8:00 AM a 6:00 PM (Lunes a Viernes)
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={`p-4 rounded-lg border ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
                <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Consultas Técnicas
                </h4>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Resuelve problemas específicos de motores, diagnósticos y reparaciones
                </p>
              </div>

              <div className={`p-4 rounded-lg border ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
                <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Sesiones de Consultoría
                </h4>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Programar sesiones de 30 minutos para casos complejos
                </p>
              </div>
            </div>

            <button className="w-full bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-all font-medium">
              <MessageCircle className="w-5 h-5 inline mr-2" />
              Iniciar Chat WhatsApp
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ===============================================
  // 📚 SECCIÓN DE DOCUMENTACIÓN
  // ===============================================

  const renderDocumentationSection = () => {
    return (
      <div className="space-y-6">
        <div className={`rounded-2xl border p-6 ${
          isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6" />
            </div>
            <div>
              <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Documentación Técnica Especializada
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Accede a manuales, procedimientos y documentación técnica premium
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {[
              { title: 'Manuales de Servicio', count: '120+', icon: FileText },
              { title: 'Diagramas Técnicos', count: '300+', icon: Settings },
              { title: 'Procedimientos', count: '250+', icon: CheckCircle },
              { title: 'Troubleshooting', count: '180+', icon: AlertCircle },
              { title: 'Especificaciones', count: '90+', icon: Database },
              { title: 'Videos Técnicos', count: '45+', icon: Clock }
            ].map((item, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border transition-all cursor-pointer ${
                  isDark 
                    ? 'border-gray-600 hover:border-gray-500 hover:bg-gray-700' 
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <item.icon className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  <span className={`text-sm font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                    {item.count}
                  </span>
                </div>
                <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {item.title}
                </h4>
              </div>
            ))}
          </div>

          <div className="flex space-x-3">
            <button className={`flex-1 py-2 px-4 rounded-lg border transition-all ${
              isDark 
                ? 'border-gray-600 hover:bg-gray-700 text-gray-300' 
                : 'border-gray-300 hover:bg-gray-50 text-gray-700'
            }`}>
              <FileText className="w-4 h-4 inline mr-2" />
              Explorar Documentos
            </button>
            <button className={`flex-1 py-2 px-4 rounded-lg border transition-all ${
              isDark 
                ? 'border-gray-600 hover:bg-gray-700 text-gray-300' 
                : 'border-gray-300 hover:bg-gray-50 text-gray-700'
            }`}>
              <Download className="w-4 h-4 inline mr-2" />
              Descargas
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ===============================================
  // 🎧 SECCIÓN DE SOPORTE
  // ===============================================

  const renderSupportSection = () => {
    const currentPlan = getCurrentPlanData();
    const isPriority = currentPlan?.limits.priority_support;

    return (
      <div className="space-y-6">
        <div className={`rounded-2xl border p-6 ${
          isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center space-x-3 mb-6">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              isPriority ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-100 text-gray-600'
            }`}>
              <HeadphonesIcon className="w-6 h-6" />
            </div>
            <div>
              <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Centro de Soporte
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {isPriority ? 'Soporte prioritario (2-4h respuesta)' : 'Soporte estándar (24-48h respuesta)'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className={`p-4 rounded-lg border ${
              isDark ? 'border-gray-600' : 'border-gray-200'
            }`}>
              <div className="flex items-center space-x-3 mb-3">
                <Mail className="w-5 h-5 text-blue-500" />
                <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Soporte por Email
                </h4>
              </div>
              <p className={`text-sm mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Envía tus consultas técnicas por email
              </p>
              <button className="text-blue-500 text-sm hover:text-blue-600 transition-all">
                soporte@kikai-tech.com
              </button>
            </div>

            <div className={`p-4 rounded-lg border ${
              isDark ? 'border-gray-600' : 'border-gray-200'
            }`}>
              <div className="flex items-center space-x-3 mb-3">
                <MessageCircle className="w-5 h-5 text-green-500" />
                <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Sistema de Tickets
                </h4>
              </div>
              <p className={`text-sm mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Crear y seguir tickets de soporte
              </p>
              <button className="text-green-500 text-sm hover:text-green-600 transition-all">
                Crear Ticket
              </button>
            </div>
          </div>

          {isPriority && (
            <div className={`p-4 rounded-lg ${
              isDark ? 'bg-yellow-900/20 border border-yellow-800' : 'bg-yellow-50 border border-yellow-200'
            } mb-6`}>
              <div className="flex items-center space-x-2 mb-2">
                <Crown className="w-5 h-5 text-yellow-600" />
                <span className={`font-medium ${isDark ? 'text-yellow-400' : 'text-yellow-800'}`}>
                  Soporte Prioritario Activado
                </span>
              </div>
              <p className={`text-sm ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>
                Tu plan incluye respuesta prioritaria en 2-4 horas y acceso a técnicos especializados
              </p>
            </div>
          )}

          <div className="space-y-3">
            <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Recursos de Ayuda:
            </h4>
            <div className="space-y-2">
              {[
                'Base de conocimientos',
                'Preguntas frecuentes',
                'Video tutoriales',
                'Guías de troubleshooting',
                'Foro de la comunidad'
              ].map((resource, index) => (
                <div
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                    isDark 
                      ? 'border-gray-600 hover:border-gray-500 hover:bg-gray-700' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {resource}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ===============================================
  // 🎨 RENDER PRINCIPAL CON NAVEGACIÓN POR PESTAÑAS
  // ===============================================

  const getCurrentPlanLimits = () => {
    const currentPlan = getCurrentPlanData();
    return currentPlan?.limits || { 
      whatsapp_support: false, 
      documentation_access: false 
    };
  };

  const renderTabNavigation = () => {
    const planLimits = getCurrentPlanLimits();
    
    const tabs = [
      { id: 'overview', label: 'Resumen', icon: User, available: true },
      { id: 'plans', label: 'Planes', icon: Crown, available: true },
      { 
        id: 'whatsapp', 
        label: 'Atención Personalizada', 
        icon: MessageCircle, 
        available: planLimits.whatsapp_support 
      },
      { 
        id: 'documentation', 
        label: 'Documentación', 
        icon: FileText, 
        available: planLimits.documentation_access 
      },
      { id: 'support', label: 'Soporte', icon: HeadphonesIcon, available: true }
    ].filter(tab => tab.available);

    return (
      <div className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <nav className="flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeSection === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-all whitespace-nowrap ${
                  isActive
                    ? isDark
                      ? 'border-blue-400 text-blue-400'
                      : 'border-blue-500 text-blue-600'
                    : isDark
                    ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>
    );
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'overview':
        return renderOverviewSection();
      case 'plans':
        return renderPlansSection();
      case 'whatsapp':
        return renderPersonalizedSupportSection();
      case 'documentation':
        return renderDocumentationSection();
      case 'support':
        return renderSupportSection();
      default:
        return renderPersonalInfoSection();
    }
  };

  // ===============================================
  // 🎨 RENDER PRINCIPAL
  // ===============================================

  if (!currentUser) {
    return (
      <div className={`flex items-center justify-center min-h-[400px] ${
        isDark ? 'text-gray-400' : 'text-gray-500'
      }`}>
        <div className="text-center">
          <User className="w-12 h-12 mx-auto mb-4" />
          <p>No hay usuario autenticado</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Toast */}
      {toastVisible && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`px-4 py-2 rounded-lg shadow-lg ${
            toastType === 'success' 
              ? 'bg-green-500 text-white' 
              : toastType === 'error'
              ? 'bg-red-500 text-white'
              : 'bg-blue-500 text-white'
          }`}>
            {toastMessage}
          </div>
        </div>
      )}

      {/* Header */}
      <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Mi Cuenta
            </h1>
            <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Gestiona tu suscripción, configuraciones y soporte técnico
            </p>
          </div>
          
          {renderTabNavigation()}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderActiveSection()}
      </div>
    </div>
  );
};

export default MyAccount;